<script lang="ts" setup>
import AppTable from '~/components/redesign/AppTable';
import Buttons from '~/components/redesign/Buttons';

// composables
import { useLangT } from '~/composables';

// types
import type { AppTableHeadProps } from '~/components/redesign/AppTable';
import type { NuxtLinkProps } from '#app/components/nuxt-link';

const props = defineProps<{
  link: Partial<NuxtLinkProps>;
  label: string;
  lang?: string;
}>();

const t = useLangT(props.lang);

const TABLE_HEAD: AppTableHeadProps = [
  {
    key: 'head',
    width: '24.6rem',
  },
  {
    key: 'capex',
    label: t('AcquisitionModels.table.header.label-2'),
    width: '23.5rem',
    align: 'center',
  },
  {
    key: 'consumption',
    label: t('AcquisitionModels.table.header.label-3'),
    width: '23.5rem',
    align: 'center',
  },
];

const TABLE_CONTENT = [
  {
    head: t('AcquisitionModels.table.content.row-1.head'),
  },
  {
    head: t('AcquisitionModels.table.content.row-2.head'),
  },
  {
    head: t('AcquisitionModels.table.content.row-3.head'),
  },
  {
    head: t('AcquisitionModels.table.content.row-4.head'),
    capex: t('AcquisitionModels.table.content.row-4.head.capex'),
    consumption: t('AcquisitionModels.table.content.row-4.head.consumption'),
  },
];
</script>

<template>
  <section data-component-name="AcquisitionModels">
    <h3 v-html="t('AcquisitionModels.title')" />

    <div class="content">
      <div class="left">
        <p v-html="t('AcquisitionModels.content-1')" />

        <strong v-html="t('AcquisitionModels.content-2')" />
      </div>

      <div class="right">
        <Buttons.Link v-bind="link">
          {{ label }}
          <SvgIcon name="arrow-forward" />
        </Buttons.Link>
      </div>
    </div>

    <AppTable.Head :head="TABLE_HEAD">
      <template #cell="{ value }">
        <div v-html="value" class="cell" />
      </template>
    </AppTable.Head>

    <AppTable.Table :head="TABLE_HEAD" :content="TABLE_CONTENT">
      <template #row="rowData">
        <AppTable.Row :head="TABLE_HEAD" :row-data="rowData">
          <template #cell-value="{ key, value }">
            <strong v-if="key === 'head'">
              {{ value }}
            </strong>

            <span v-else-if="isString(value)">
              {{ value }}
            </span>

            <SvgIcon v-else name="check-in-circle-fill" />
          </template>
        </AppTable.Row>
      </template>
    </AppTable.Table>

    <ul class="cards">
      <li v-for="columnIndex in 2" :key="columnIndex" class="card">
        <ul class="rows">
          <li v-html="TABLE_HEAD[columnIndex].label" class="heading" />

          <template
            v-for="(row, rowIndex) in TABLE_CONTENT"
            :key="`${columnIndex}-${rowIndex}`"
          >
            <li class="row">
              <div class="left">
                <strong v-if="'head' in row" v-html="row.head" />
              </div>

              <div class="right">
                <span
                  v-if="Object.values(row).length > 1"
                  v-html="Object.values(row)[columnIndex]"
                />

                <span v-else class="circle">
                  <SvgIcon name="check" />
                </span>
              </div>
            </li>
          </template>
        </ul>
      </li>
    </ul>

    <p v-html="t('AcquisitionModels.table.row-5')" class="available-in-usa" />
  </section>
</template>

<style lang="scss" scoped>
@use '_/fn';
@use '_/mixins/flex';
@use '_/mixins/media';
@use '_/mixins/size';
@use '_/mixins/typo';

[data-component-name='AcquisitionModels'] {
  @include flex.stretch-start;
  flex-direction: column;

  h3 {
    @include typo.h3;
    max-width: 27ch;
    margin-bottom: 2rem;

    @include media.tablet {
      max-width: 100%;
    }

    @include media.mobile {
      margin-bottom: 1.5rem;
    }
  }

  .content {
    @include flex.stretch-sb;
    margin-bottom: 2rem;

    @include media.tablet {
      flex-direction: column;
    }

    @include media.mobile {
      margin-bottom: 1.5rem;
    }

    .left {
      p {
        @include typo.body-2;
        margin-bottom: 1rem;
        max-width: 72ch;

        @include media.tablet {
          max-width: 100%;
        }
      }

      strong {
        @include typo.subtitle-2;

        display: block;
        max-width: 58ch;

        @include media.tablet {
          max-width: 100%;
        }
      }
    }

    .right {
      [data-component-name='Buttons.Link'] {
        display: inline-block;
        min-width: 16.25rem;

        @include media.tablet {
          margin-top: 2rem;
          float: right;
        }

        @include media.mobile {
          display: block;
          width: 100%;
          margin-bottom: 0.5rem;
        }
      }
    }
  }

  [data-component-name='AppTable.Head'],
  [data-component-name='AppTable.Table'] {
    @include media.mobile {
      display: none;
    }
  }

  :deep([data-component-name='AppTable.Head']) {
    padding: 0;
    border-bottom: unset;
    margin-bottom: 1rem;

    li:not(:first-of-type) {
      .cell {
        @include size.full;

        @include flex.center-center;
        flex-direction: column;
        gap: 1rem 0;
        padding: 1rem;

        background-color: fn.token('surf-cont-secondary');
        text-align: center;

        strong {
          @include typo.h4;
          word-break: normal;
          overflow-wrap: normal;
        }

        span {
          @include typo.subtitle-3;
        }
      }
    }
  }

  [data-component-name='AppTable.Table'] {
    margin-bottom: 1rem;

    [data-component-name='AppTable.Row'] {
      padding: 0.5rem 0;

      strong {
        @include typo.subtitle-3;
      }

      span {
        @include typo.body-3;
      }

      svg {
        @include size.fixed(1.5rem);
        fill: fn.token('icon-action');
      }
    }
  }

  ul.cards {
    display: none;

    @include media.mobile {
      @include flex.stretch-start;
      flex-direction: column;
      gap: 1rem;
    }

    li.card {
      ul.rows {
        li.heading {
          padding: 2rem;
          background-color: fn.token('surf-cont-secondary');
          text-align: center;

          margin-bottom: 1rem;

          :deep(strong) {
            @include typo.h4;
            display: block;
            margin-bottom: 1rem;
          }

          :deep(span) {
            @include typo.subtitle-3;
            margin-top: 1rem;
          }
        }

        li.row {
          padding: 1rem 0;
          @include flex.center-sb;
          gap: 0.5rem;

          border-bottom: 1px solid fn.token('outline-secondary');

          .left {
            max-width: 50%;
            width: 100%;

            strong {
              @include typo.subtitle-3;
            }
          }

          .right {
            max-width: 50%;
            width: 100%;

            @include flex.center-center;

            span.circle {
              display: inline-block;

              @include size.fixed(1.5rem);
              padding: 2px;

              svg {
                fill: fn.token('icon-reversed');
                background-color: fn.token('icon-action');
                border-radius: 100px;
              }
            }

            span:not(.circle) {
              @include typo.body-4;
              text-align: center;
            }
          }
        }
      }
    }
  }

  p.available-in-usa {
    @include typo.body-4;
  }
}
</style>
